import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { PERMISSIONS, PLAN_STATUSES } from '@solar/contracts';

import {
  AdminIcon,
  ManagementHeader,
  MetricCard,
  adminManagementStyles as styles,
} from '../../../components/admin/AdminManagement';
import { Alert } from '../../../components/Alert';
import { Button } from '../../../components/Button';
import { useAuth } from '../../../features/auth/auth-context';
import { catalogueApi } from '../../../features/catalogue/catalogue.api';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatNgn } from '../../../lib/format-currency';
import { formatStatusLabel } from '../../../lib/format-status';
import { paths, routeTo } from '../../../routes/paths';

function formatDeposit(type: string, value: string): string {
  return type === 'PERCENTAGE' ? `${value}%` : formatNgn(value);
}

export function AdminRepaymentPlansPage(): React.JSX.Element {
  const { can } = useAuth();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const plans = useQuery({
    queryKey: ['admin', 'repayment-plans', { page, status }],
    queryFn: () =>
      catalogueApi.listRepaymentPlans({ page, pageSize: 20, status: status || undefined }),
  });
  const items = plans.data?.items ?? [];

  return (
    <div className={styles.page}>
      <ManagementHeader
        eyebrow="Catalogue management"
        title="Repayment plans"
        subtitle="Configure transparent payment options for eligible solar products."
        icon="plan"
        context="Plan templates"
        action={
          can(PERMISSIONS.PLANS_MANAGE) ? (
            <Link className={styles.buttonLink} to={paths.adminRepaymentPlanCreate}>
              Create plan <AdminIcon name="arrow" />
            </Link>
          ) : undefined
        }
      />
      <section className={styles.hero}>
        <div>
          <h2>Flexible terms, clearly configured</h2>
          <p>Every plan shown here is sourced from the repayment-plan service.</p>
        </div>
        <strong>
          {plans.data
            ? `${plans.data.pagination.totalItems} ${plans.data.pagination.totalItems === 1 ? 'plan' : 'plans'}`
            : 'Plan catalogue'}
        </strong>
      </section>
      <section className={styles.metrics}>
        <MetricCard
          label="Plan templates"
          value={plans.data?.pagination.totalItems ?? '—'}
          icon="plan"
        />
        <MetricCard
          label="Current filter"
          value={status === '' ? 'All' : formatStatusLabel(status)}
          icon="filter"
          tone="blue"
        />
        <MetricCard
          label="Page"
          value={plans.data?.pagination.page ?? '—'}
          icon="document"
          tone="amber"
        />
        <MetricCard label="Displayed" value={items.length} icon="check" tone="violet" />
      </section>
      {items.length > 0 && (
        <section aria-labelledby="plan-options-heading">
          <header className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <span className={styles.sectionIcon}>
                <AdminIcon name="spark" />
              </span>
              <div>
                <h2 id="plan-options-heading">Plan options</h2>
                <p>Real templates from the current result set</p>
              </div>
            </div>
          </header>
          <div className={styles.cardsGrid}>
            {items.slice(0, 3).map((plan) => (
              <article className={styles.planCard} key={plan.id}>
                <span className={styles.reportIcon}>
                  <AdminIcon name="calendar" />
                </span>
                <h2>{plan.name}</h2>
                <p>{plan.description ?? 'Repayment template configured for eligible products.'}</p>
                <div className={styles.planMeta}>
                  <span>
                    <small>Tenure</small>
                    <strong>{plan.tenureMonths} months</strong>
                  </span>
                  <span>
                    <small>Deposit</small>
                    <strong>{formatDeposit(plan.depositType, plan.depositValue)}</strong>
                  </span>
                </div>
                <Link
                  className={`${styles.buttonLink} ${styles.buttonSecondary}`}
                  to={routeTo.adminRepaymentPlan(plan.id)}
                >
                  View configuration
                </Link>
              </article>
            ))}
          </div>
        </section>
      )}
      <form className={styles.filterPanel}>
        <label>
          Status
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
            }}
          >
            <option value="">All statuses</option>
            {PLAN_STATUSES.map((value) => (
              <option value={value} key={value}>
                {formatStatusLabel(value)}
              </option>
            ))}
          </select>
        </label>
      </form>
      <section className={styles.mainCard}>
        <header className={styles.sectionHeader}>
          <div className={styles.sectionTitle}>
            <span className={styles.sectionIcon}>
              <AdminIcon name="plan" />
            </span>
            <div>
              <h2>All repayment plans</h2>
              <p>Commercial settings and availability</p>
            </div>
          </div>
        </header>
        {plans.isPending && <p className={styles.empty}>Loading plans…</p>}
        {plans.isError && (
          <div className={styles.alertWrap}>
            <Alert tone="error">{toDisplayMessage(plans.error)}</Alert>
          </div>
        )}
        {plans.isSuccess && items.length === 0 && (
          <p className={styles.empty}>No repayment plans match this status.</p>
        )}
        {items.length > 0 && (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Deposit</th>
                  <th>Tenure</th>
                  <th>Frequency</th>
                  <th>Product price band</th>
                  <th>Method</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {items.map((plan) => (
                  <tr key={plan.id}>
                    <td>
                      <div className={styles.primaryCell}>
                        <span className={styles.avatar}>
                          <AdminIcon name="plan" />
                        </span>
                        <span>
                          <strong>{plan.name}</strong>
                          <small>{plan.code}</small>
                        </span>
                      </div>
                    </td>
                    <td>{formatDeposit(plan.depositType, plan.depositValue)}</td>
                    <td>{plan.tenureMonths} months</td>
                    <td>{formatStatusLabel(plan.repaymentFrequency)}</td>
                    <td>
                      {plan.minProductPrice === null
                        ? 'No minimum'
                        : formatNgn(plan.minProductPrice)}
                      <span className={styles.muted}>
                        {' '}
                        {plan.maxProductPrice === null
                          ? '· No maximum'
                          : `– ${formatNgn(plan.maxProductPrice)}`}
                      </span>
                    </td>
                    <td>{formatStatusLabel(plan.calculationMethod)}</td>
                    <td>
                      <span
                        className={`${styles.status} ${plan.status === 'ACTIVE' ? styles.success : styles.warning}`}
                      >
                        {formatStatusLabel(plan.status)}
                      </span>
                    </td>
                    <td>
                      <Link to={routeTo.adminRepaymentPlan(plan.id)}>Manage →</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {plans.data && plans.data.pagination.totalPages > 1 && (
          <nav className={styles.pagination}>
            <Button
              variant="secondary"
              disabled={!plans.data.pagination.hasPreviousPage}
              onClick={() => setPage((value) => value - 1)}
            >
              Previous
            </Button>
            <span>
              Page {plans.data.pagination.page} of {plans.data.pagination.totalPages}
            </span>
            <Button
              variant="secondary"
              disabled={!plans.data.pagination.hasNextPage}
              onClick={() => setPage((value) => value + 1)}
            >
              Next
            </Button>
          </nav>
        )}
      </section>
    </div>
  );
}
